import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, CheckCircle, X } from "@phosphor-icons/react";

const FALLBACK = {
  starter: { name: "Starter", price_inr: 0, interval: "free", case_limit: 25, user_limit: 3,
    features: ["Up to 25 cases", "3 team members", "Basic case management", "Manual evidence chain", "Standard reports"],
    cta: "Start free" },
  pro: { name: "Pro", price_inr: 99000, interval: "month", case_limit: 250, user_limit: 25,
    features: ["Up to 250 cases", "25 team members", "All case codes", "Court-admissible evidence chain", "PDF & CSV exports", "IntelliProbe AI OSINT", "Monthly auto re-scans", "Priority email support"],
    cta: "Start 14-day trial" },
  enterprise: { name: "Enterprise", price_inr: 499000, interval: "month", case_limit: -1, user_limit: -1,
    features: ["Unlimited cases & users", "Multi-organization tenancy", "API + webhooks", "Custom RACI", "White-glove onboarding", "Dedicated CSM", "99.9% SLA", "On-prem deployment option"],
    cta: "Talk to sales" },
};

const COMPARE = [
  ["Cases",          "25",        "250",   "Unlimited"],
  ["Team members",   "3",         "25",    "Unlimited"],
  ["RBAC matrix",    true,        true,    true],
  ["Evidence chain", true,        true,    true],
  ["Court-bundle PDF", false,     true,    true],
  ["IntelliProbe AI OSINT", false, true,   true],
  ["CSV exports",    false,       true,    true],
  ["API + webhooks", false,       false,   true],
  ["Multi-org tenancy", false,    false,   true],
  ["Dedicated CSM",  false,       false,   true],
  ["SLA",            "Best effort", "99.5%", "99.9%"],
];

function Tick({ on }) {
  return on ? <CheckCircle size={16} weight="bold" /> : <X size={14} weight="bold" className="opacity-30" />;
}

export default function Pricing() {
  const [plans, setPlans] = useState(FALLBACK);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data || FALLBACK)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F7F8] text-[#0A0B0D]" data-testid="pricing-page">
      {/* ---- Top nav ---- */}
      <nav className="border-b border-[#E5E5EA] bg-white sticky top-0 z-30">
        <div className="max-w-[1320px] mx-auto h-14 px-5 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-home-pricing">
            <img src="/api/static/brand/C2_Horizontal_White.svg" alt="SPAN CasePrime" style={{ width: "220px", height: "auto" }} />
          </Link>
          <div className="flex items-center gap-4 text-[12px]">
            <Link to="/" className="hover:text-[#0A0B0D]/70">Home</Link>
            <Link to="/health-score" className="hover:text-[#0A0B0D]/70">Health Score</Link>
            <Link to="/login" className="hover:text-[#0A0B0D]/70">Sign in</Link>
          </div>
        </div>
      </nav>

      {/* ---- Hero ---- */}
      <section className="border-b border-[#E5E5EA] py-16 text-center">
        <div className="max-w-[800px] mx-auto px-5">
          <div className="label-eyebrow mb-3">PRICING · BILLED IN INR · STRIPE</div>
          <h1 className="font-display font-bold text-[44px] sm:text-[64px] leading-[0.98] tracking-tight">
            Pricing that scales<br/>with your <span className="bg-[#0A0B0D] text-white px-2">case load.</span>
          </h1>
          <p className="text-[16px] text-[#475467] mt-5">
            Start on Starter free, upgrade when you cross 25 cases. Pro is a 14-day money-back trial.
          </p>
        </div>
      </section>

      {/* ---- Plan cards ---- */}
      <section className="py-14 border-b border-[#E5E5EA]">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-5">
          {(["starter", "pro", "enterprise"]).map((key) => {
            const p = plans[key] || FALLBACK[key];
            const isPro = key === "pro";
            return (
              <div
                key={key}
                className={isPro
                  ? "border-2 border-black bg-[#0A0B0D] text-white p-7 relative"
                  : "border border-[#E5E5EA] bg-white p-7 relative"
                }
                data-testid={`plan-card-${key}`}
              >
                {isPro && (
                  <div className="absolute -top-3 left-7 bg-white text-[#0A0B0D] text-[10px] font-mono uppercase tracking-[0.22em] px-2 py-1 border-2 border-black">
                    ◆ Most popular
                  </div>
                )}
                <div className="label-eyebrow" style={isPro ? {color: "rgba(255,255,255,0.6)"} : {}}>
                  {p.interval === "free" ? "Free tier" : "Per month"}
                </div>
                <div className="font-display font-bold text-[28px] mt-1">{p.name}</div>
                <div className="font-display font-bold text-[44px] tnum leading-none mt-3">
                  {p.price_inr === 0 ? "Free" : `₹${p.price_inr.toLocaleString("en-IN")}`}
                  {p.interval === "month" && <span className="text-[14px] font-mono opacity-70 ml-1">/month</span>}
                </div>
                {p.case_limit !== -1 ? (
                  <div className="text-[11px] font-mono uppercase tracking-[0.18em] mt-2 opacity-70">
                    Up to {p.case_limit} cases · {p.user_limit} users
                  </div>
                ) : (
                  <div className="text-[11px] font-mono uppercase tracking-[0.18em] mt-2 opacity-70">
                    Unlimited everything
                  </div>
                )}
                <div className="my-5 h-px" style={isPro ? {background: "rgba(255,255,255,0.15)"} : {background: "#E5E5EA"}} />
                <ul className="space-y-2.5 text-[13px]">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle size={14} weight="bold" className="shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={key === "enterprise" ? "mailto:pankaj@span.com" : key === "starter" ? "/login" : "/login?next=/billing"}
                  className={"span-btn mt-7 w-full justify-center " + (isPro ? "" : "")}
                  style={isPro ? {background: "#fff", color: "#000", borderColor: "#fff"} : {}}
                  data-testid={`plan-cta-${key}`}
                >
                  {p.cta} <ArrowRight size={14} />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---- Compare table ---- */}
      <section className="py-16 border-b border-[#E5E5EA] bg-white">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="label-eyebrow mb-3">COMPARE</div>
          <h2 className="font-display font-bold text-[32px] lg:text-[40px] tracking-tight mb-8">Every feature, side by side.</h2>
          <div className="overflow-x-auto -mx-5 lg:mx-0 px-5 lg:px-0">
            <table className="w-full text-[13px] border-collapse" data-testid="pricing-compare">
              <thead>
                <tr className="border-y border-[#0A0B0D]">
                  <th className="text-left py-4 pr-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#475467] w-[42%]">Feature</th>
                  <th className="py-4 px-2 font-display font-bold text-[15px] text-left">Starter</th>
                  <th className="py-4 px-2 font-display font-bold text-[15px] text-left">Pro</th>
                  <th className="py-4 px-2 font-display font-bold text-[15px] text-left">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((row, i) => (
                  <tr key={i} className="border-b border-[#E5E5EA]">
                    <td className="py-3 pr-4 text-[#475467]">{row[0]}</td>
                    {[row[1], row[2], row[3]].map((v, j) => (
                      <td key={j} className="py-3 px-2 align-middle">
                        {typeof v === "boolean" ? <Tick on={v} /> : <span className="font-mono text-[12px]">{v}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ---- FAQ ---- */}
      <section className="py-16 border-b border-[#E5E5EA]">
        <div className="max-w-[800px] mx-auto px-5">
          <div className="label-eyebrow mb-3">FAQ</div>
          <h2 className="font-display font-bold text-[32px] tracking-tight mb-8">Common questions</h2>
          <div className="space-y-6">
            {[
              {q: "Is the 14-day trial really free?",
               a: "Yes. We charge ₹99,000 only after day 14. Cancel any time before then from the Billing page — no charge."},
              {q: "What payment methods do you accept?",
               a: "Stripe — all major credit/debit cards, UPI, and net banking. Annual contracts on Enterprise can be billed via wire / NEFT against a PO."},
              {q: "Where is data stored?",
               a: "Mumbai-region MongoDB Atlas with daily encrypted backups. On-prem deployment available on Enterprise."},
              {q: "Is the evidence really court-admissible?",
               a: "Yes — every evidence item carries a SHA-256 of the file bytes plus an HMAC-SHA256 cryptographic anchor verifiable offline via the SPAN_PROOF_SECRET. Bundles are exportable as ISO-27035-aligned PDFs."},
              {q: "Can you integrate with our existing ERP or DMS?",
               a: "API + webhooks ship with Enterprise. Out of the box we expose REST for cases, invoices, payables and evidence."},
            ].map((f) => (
              <div key={f.q} className="border-b border-[#E5E5EA] pb-6">
                <div className="font-display font-bold text-[16px]">{f.q}</div>
                <div className="text-[14px] text-[#475467] mt-2 leading-[1.55]">{f.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-10 bg-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#475467]">© SPAN CasePrime · A SPAN CONSULTING PRODUCT</div>
          <div className="flex items-center gap-5 text-[12px] text-[#475467]">
            <Link to="/">Home</Link>
            <Link to="/health-score">Health Score</Link>
            <Link to="/login">Operator console</Link>
            <a href="mailto:pankaj@span.com">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
